import React from 'react';
import type { Deployment } from '../api/campaignsApi';
import {
  type GoogleSearchData,
  type CampaignDemographics,
  AGE_LABELS,
  GENDER_LABELS,
  INCOME_LABELS,
} from './types';
import { EditableList, CollapsibleSection, MonthlyBudgetField } from './ReviewComponents';
import { useManagementMode } from './useManagementMode';
import styles from './ReviewPanel.module.css';

/**
 * Editable demographic targeting. Each dimension shows every bracket as a chip;
 * an active chip is *included* (served). Google defaults to all-on, so all
 * chips active means no restriction — the value is stored as the included list
 * only when a dimension is narrowed, matching how the launcher and file behave.
 */
function DemographicsEditor({
  demographics,
  onChange,
  disabled,
}: {
  demographics: CampaignDemographics | undefined;
  onChange: (next: CampaignDemographics | undefined) => void;
  disabled: boolean;
}) {
  const groups: { key: keyof CampaignDemographics; label: string; all: readonly string[] }[] = [
    { key: 'ageRanges', label: 'Age', all: AGE_LABELS },
    { key: 'genders', label: 'Gender', all: GENDER_LABELS },
    { key: 'householdIncomes', label: 'Household income', all: INCOME_LABELS },
  ];

  // A dimension with no stored list means "all included" — show every chip active.
  const includedFor = (key: keyof CampaignDemographics, all: readonly string[]): Set<string> => {
    const stored = demographics?.[key];
    return new Set(stored && stored.length > 0 ? stored : all);
  };

  const toggle = (key: keyof CampaignDemographics, all: readonly string[], label: string) => {
    const included = includedFor(key, all);
    if (included.has(label)) included.delete(label);
    else included.add(label);
    // Keep canonical order; drop the whole restriction when everything is on.
    const orderedIncluded = all.filter((l) => included.has(l));
    const next: CampaignDemographics = { ...demographics };
    if (orderedIncluded.length === 0 || orderedIncluded.length === all.length) {
      delete next[key];
    } else {
      next[key] = orderedIncluded;
    }
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };

  return (
    <div>
      {groups.map(({ key, label, all }) => {
        const included = includedFor(key, all);
        return (
          <div key={key} className={styles.demoGroup}>
            <span className={styles.demoGroupLabel}>{label}</span>
            <div className={styles.tagList}>
              {all.map((bracket) => {
                const active = included.has(bracket);
                return (
                  <button
                    key={bracket}
                    type="button"
                    className={`${styles.demoChip} ${active ? styles.demoChipActive : ''}`}
                    onClick={() => toggle(key, all, bracket)}
                    disabled={disabled}
                    aria-pressed={active}
                  >
                    {bracket}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
      <p className={styles.demoNote}>
        Deselect a bracket to exclude it. All selected means no restriction (Google&apos;s default).
        Excluded brackets are applied to Google when this campaign launches.
      </p>
    </div>
  );
}

interface Props {
  deployment: Deployment;
  onSave: (platformData: GoogleSearchData) => void;
  isSaving: boolean;
}

/** Short summary for the collapsed Demographics section header. */
function demographicsMeta(demographics: CampaignDemographics | undefined): string {
  const narrowed = [
    demographics?.ageRanges?.length ? 'age' : '',
    demographics?.genders?.length ? 'gender' : '',
    demographics?.householdIncomes?.length ? 'income' : '',
  ].filter(Boolean);
  return narrowed.length === 0 ? 'all' : `narrowed: ${narrowed.join(', ')}`;
}

export function GoogleSearchReview({ deployment, onSave, isSaving }: Props) {
  const isAutopilot = useManagementMode() === 'autopilot';
  const raw = deployment.platformData as Partial<GoogleSearchData> | null | undefined;
  if (!raw) return <p className={styles.deliveryNote}>No data generated yet.</p>;
  // Defensive read: imported/synced campaigns and legacy rows may omit list
  // fields the editor renders directly. Normalize so a missing headlines /
  // descriptions array never crashes the page to blank.
  const data = {
    ...raw,
    headlines: Array.isArray(raw.headlines) ? raw.headlines : [],
    descriptions: Array.isArray(raw.descriptions) ? raw.descriptions : [],
  } as GoogleSearchData;
  const isLive = deployment.deployStatus === 'live';

  const update = (
    field: keyof GoogleSearchData,
    value: GoogleSearchData[keyof GoogleSearchData],
  ) => {
    onSave({ ...data, [field]: value });
  };

  return (
    <div className={styles.panel}>
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Campaign Overview</h3>
        <div className={styles.metaRow}>
          <MonthlyBudgetField
            dailyBudget={data.dailyBudget}
            onSave={(nextDaily) => update('dailyBudget', nextDaily)}
            disabled={isLive || isAutopilot}
          />
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.bidStrategy?.replace(/_/g, ' ')}</span>
            <span className={styles.metaLabel}>Bid strategy</span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{(data.zipCodes ?? []).join(', ')}</span>
            <span className={styles.metaLabel}>Zip codes</span>
          </div>
        </div>
      </section>

      {/* Shown for every Google Search campaign — native, skills-created and
          imported alike — so the effective delivery/targeting settings are
          always visible. Native campaigns render Google's defaults (unset
          fields); imports render the values carried from the file. */}
      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Delivery &amp; Targeting</h3>
        <div className={styles.metaRow}>
          <div className={styles.metaStat}>
            {/* Once launched, show the real serving state (kept in sync with
                Google), not the status the campaign was imported with. */}
            <span className={styles.metaValue}>
              {isLive
                ? deployment.status === 'active'
                  ? 'Serving'
                  : 'Paused'
                : data.status === 'PAUSED'
                  ? 'Paused'
                  : 'Enabled'}
            </span>
            <span className={styles.metaLabel}>
              {isLive ? 'Status on Google' : 'Launch status'}
            </span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.searchPartners ? 'On' : 'Off'}</span>
            <span className={styles.metaLabel}>Search partners</span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>{data.displayNetwork ? 'On' : 'Off'}</span>
            <span className={styles.metaLabel}>Display network</span>
          </div>
          <div className={styles.metaStat}>
            <span className={styles.metaValue}>
              {data.locationTargeting === 'PRESENCE' ? 'Presence' : 'Presence or interest'}
            </span>
            <span className={styles.metaLabel}>Location targeting</span>
          </div>
          {(data.languages ?? []).length > 0 && (
            <div className={styles.metaStat}>
              <span className={styles.metaValue}>{(data.languages ?? []).join(', ')}</span>
              <span className={styles.metaLabel}>Languages</span>
            </div>
          )}
        </div>
        {!isLive && (
          <p className={styles.deliveryNote}>
            These settings transfer to Google when this campaign is launched.
          </p>
        )}
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Ad Preview</h3>
        <div className={styles.adPreview}>
          <div className={styles.adUrl}>{data.finalUrl}</div>
          <div className={styles.adHeadlines}>{data.headlines.slice(0, 3).join(' | ')}</div>
          <div className={styles.adDesc}>{data.descriptions[0]}</div>
        </div>
      </section>

      <CollapsibleSection
        title="Headlines"
        meta={`${data.headlines.length}/15 · max 30 chars`}
        defaultOpen
      >
        <EditableList
          items={data.headlines}
          maxChar={30}
          onUpdate={(v) => update('headlines', v)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title="Descriptions"
        meta={`${data.descriptions.length}/4 · max 90 chars`}
        defaultOpen
      >
        <EditableList
          items={data.descriptions}
          maxChar={90}
          onUpdate={(v) => update('descriptions', v)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      <CollapsibleSection title="Keywords" meta={`${data.keywords?.length ?? 0} keywords`}>
        <div className={styles.keywordTable}>
          {(data.keywords ?? []).map((kw, i) => (
            <div key={i} className={styles.keywordRow}>
              <span className={`${styles.matchBadge} ${styles[`match_${kw.matchType}`]}`}>
                {kw.matchType}
              </span>
              <span className={styles.keywordText}>{kw.keyword}</span>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Negative Keywords"
        meta={`${(data.negativeKeywords ?? []).length}`}
      >
        <div className={styles.tagList}>
          {(data.negativeKeywords ?? []).map((kw, i) => (
            <span key={i} className={styles.negativeTag}>
              {kw}
            </span>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Sitelinks" meta={`${(data.sitelinks ?? []).length}`}>
        <div className={styles.sitelinkList}>
          {(data.sitelinks ?? []).map((sl, i) => (
            <div key={i} className={styles.sitelinkCard}>
              <div className={styles.sitelinkText}>{sl.text}</div>
              <div className={styles.sitelinkDesc}>{sl.description1}</div>
              <div className={styles.sitelinkDesc}>{sl.description2}</div>
              <div className={styles.sitelinkUrl}>{sl.finalUrl}</div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Callout Extensions" meta={`${(data.callouts ?? []).length}`}>
        <div className={styles.tagList}>
          {(data.callouts ?? []).map((c, i) => (
            <span key={i} className={styles.calloutTag}>
              {c}
            </span>
          ))}
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Call assets" meta={`${(data.callAssets ?? []).length}`}>
        {(data.callAssets ?? []).length > 0 ? (
          <div className={styles.tagList}>
            {(data.callAssets ?? []).map((phone) => (
              <span key={phone} className={styles.calloutTag}>
                {phone}
              </span>
            ))}
          </div>
        ) : (
          <p className={styles.deliveryNote}>
            No phone number on the ads, so &ldquo;Calls from ads&rdquo; can&rsquo;t be counted.
          </p>
        )}
      </CollapsibleSection>

      <CollapsibleSection title="Demographics" meta={demographicsMeta(data.demographics)}>
        <DemographicsEditor
          demographics={data.demographics}
          onChange={(next) => update('demographics', next)}
          disabled={isLive || isAutopilot}
        />
      </CollapsibleSection>

      {isSaving && <p className={styles.savingNote}>Saving…</p>}
    </div>
  );
}
